import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  Save,
  Sun,
  Umbrella,
  Coffee,
  Stethoscope,
  Plus,
  X,
  AlertTriangle,
  BookOpen,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { timesheetsApi } from "../api/timesheets";
import { MONTH_NAMES } from "../types";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import { DraftHistoryTimeline } from "./DraftHistoryTimeline";
import { downloadBlob } from "@/utils/download";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

function buildPeriods() {
  const periods: { year: number; month: number; label: string }[] = [];
  // Previous, current and next month
  for (let i = 1; i >= -1; i--) {
    let m = CURRENT_MONTH - i;
    let y = CURRENT_YEAR;
    if (m <= 0) {
      m += 12;
      y -= 1;
    } else if (m > 12) {
      m -= 12;
      y += 1;
    }
    periods.push({ year: y, month: m, label: `${MONTH_NAMES[m]} ${y}` });
  }
  return periods;
}
const PERIODS = buildPeriods();

const UZ_PRESETS = [
  { from: "06:00", to: "14:00", label: "6–14" },
  { from: "06:30", to: "14:30", label: "6:30–14:30" },
  { from: "07:00", to: "15:00", label: "7–15" },
  { from: "07:30", to: "15:30", label: "7:30–15:30" },
  { from: "08:00", to: "16:00", label: "8–16" },
  { from: "08:30", to: "16:30", label: "8:30–16:30" },
  { from: "09:00", to: "17:00", label: "9–17" },
] as const;

interface PreviewDay {
  day: number;
  date_str: string;
  weekday_short: string;
  is_weekend: boolean;
  is_holiday: boolean;
  /** Day precedes the employee's employment start — locked, rendered as "—". */
  before_employment?: boolean;
  leave_label: string;
  other_leave_label: string;
  sick_leave_label: string;
  auto_note: string;
  is_working: boolean;
}

interface TimeRange {
  from: string;
  to: string;
}

interface DayState {
  shift: 7 | 8 | 9 | null;
  ranges: TimeRange[];
  extra_note: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set the modal generates a PDF for this employee instead of the logged-in user. */
  forEmployee?: { id: string; name: string } | null;
}

function toMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function rangeHours(from: string, to: string): number {
  if (!from || !to) return 0;
  const mins = toMinutes(to) - toMinutes(from);
  return mins > 0 ? mins / 60 : 0;
}

function formatHours(h: number): string {
  if (h <= 0) return "";
  const r = Math.round(h * 10) / 10;
  const totalMins = Math.round(r * 60);
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins === 0 ? `${hrs}h` : `${hrs}:${String(mins).padStart(2, "0")}`;
}

/** Returns indices of ranges that participate in at least one overlap with another range. */
function getOverlappingIndices(ranges: TimeRange[]): Set<number> {
  const result = new Set<number>();
  const parsed = ranges.map((r) => {
    if (!r.from || !r.to) return null;
    const start = toMinutes(r.from);
    const end = toMinutes(r.to);
    return end > start ? [start, end] : null;
  });
  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      const a = parsed[i];
      const b = parsed[j];
      if (!a || !b) continue;
      if (a[0] < b[1] && b[0] < a[1]) {
        result.add(i);
        result.add(j);
      }
    }
  }
  return result;
}

export function TimesheetPdfModal({ open, onClose, forEmployee }: Props) {
  const employeeId = forEmployee?.id ?? null;
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [defaultShift, setDefaultShift] = useState<7 | 8 | 9>(8);
  const [isUz, setIsUz] = useState(false);
  const [dayStates, setDayStates] = useState<Record<number, DayState>>({});
  const [dayUpdatedAt, setDayUpdatedAt] = useState<Record<string, string>>({});
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  // null = primary contract, uuid = a specific secondary UZ contract.
  const [secondaryContractId, setSecondaryContractId] = useState<string | null>(
    null,
  );
  const [bulkDownloading, setBulkDownloading] = useState(false);
  // Flat mode for additional UZ — just a single declared total.
  const [declaredHours, setDeclaredHours] = useState<string>("");

  const isSecondaryUz = !!secondaryContractId;

  const queryClient = useQueryClient();
  const initializedPeriodRef = useRef<string>("");

  // Active secondary UZ contracts + primary contract info (kind + company) for
  // the current employee (or the on-behalf target) in this period.
  const { data: contractsPayload } = useQuery({
    queryKey: ["timesheet-my-secondary-with-primary", year, month, employeeId],
    queryFn: () => timesheetsApi.mySecondaryContractsWithPrimary(year, month, employeeId),
    enabled: open,
  });
  const secondaryContracts = contractsPayload?.secondaries ?? [];
  const primaryInfo = contractsPayload?.primary;
  const fractionNum = primaryInfo?.employment_fraction_numerator ?? 1;
  const fractionDen = primaryInfo?.employment_fraction_denominator ?? 1;
  const isPartTimeUop = !isUz && !isSecondaryUz && fractionNum < fractionDen;
  const usesRanges = isUz || isPartTimeUop;

  const PRIMARY_LABELS: Record<string, string> = {
    uop: "Umowa o pracę",
    uz: "Umowa zlecenie",
    b2b: "Kontrakt B2B",
  };
  const primaryLabel = (() => {
    const base = PRIMARY_LABELS[primaryInfo?.contract_type ?? "uop"] ?? "Umowa";
    const company =
      primaryInfo?.company_short_code ?? primaryInfo?.company_name;
    return company ? `${base} — ${company}` : base;
  })();

  // When the period changes and a previously-selected secondary contract is no
  // longer active, drop the selection.
  useEffect(() => {
    if (!open) return;
    if (
      secondaryContractId &&
      !secondaryContracts.some((c) => c.id === secondaryContractId)
    ) {
      setSecondaryContractId(null);
      initializedPeriodRef.current = "";
    }
  }, [open, year, month, secondaryContracts, secondaryContractId]);

  const { data: preview, isLoading } = useQuery({
    queryKey: ["timesheet-pdf-preview", year, month, secondaryContractId, employeeId],
    queryFn: () => timesheetsApi.pdfPreview(year, month, secondaryContractId, employeeId),
    enabled: open,
    staleTime: 0,
  });

  const { data: draft, isLoading: isDraftLoading } = useQuery({
    queryKey: ["timesheet-pdf-draft", year, month, secondaryContractId, employeeId],
    queryFn: () => timesheetsApi.getPdfDraft(year, month, secondaryContractId, employeeId),
    enabled: open,
    staleTime: 0,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      timesheetsApi.savePdfDraft({
        year,
        month,
        default_shift: isSecondaryUz || usesRanges ? null : defaultShift,
        day_states: isSecondaryUz
          ? {}
          : (Object.fromEntries(
              Object.entries(dayStates).map(([k, v]) => [k, v]),
            ) as Record<string, unknown>),
        declared_hours: isSecondaryUz ? declaredHours || null : null,
        secondary_contract: secondaryContractId,
        employee: employeeId,
      }),
    onSuccess: () => {
      initializedPeriodRef.current = "";
      queryClient.invalidateQueries({
        queryKey: ["timesheet-pdf-draft", year, month, secondaryContractId, employeeId],
      });
      // Refresh the audit timeline immediately so the user sees their new entry.
      queryClient.invalidateQueries({
        queryKey: ["timesheets", "draft-history"],
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (err: unknown) => {
      // @ts-expect-error axios response shape
      const detail = err?.response?.data?.errors?.hours_overlap
        ? "Kolizja godzin UoP × UZ — popraw przedziały zanim zapiszesz."
        : "Błąd podczas zapisywania. Spróbuj ponownie.";
      setError(detail);
    },
  });

  useEffect(() => {
    if (!open || !preview?.days || isDraftLoading) return;
    const periodKey = `${year}-${month}-${secondaryContractId ?? "primary"}`;
    if (initializedPeriodRef.current === periodKey) return;
    initializedPeriodRef.current = periodKey;

    const uz = preview.is_uz ?? false;
    setIsUz(uz);

    // Restore declared-hours value (only meaningful for secondary UZ).
    setDeclaredHours(
      secondaryContractId &&
        draft &&
        (draft as { declared_hours?: string | null }).declared_hours != null
        ? String((draft as { declared_hours?: string }).declared_hours)
        : "",
    );

    if (draft) {
      const savedShift = (draft.default_shift as 7 | 8 | 9 | null) ?? 8;
      if (!uz && draft.default_shift) setDefaultShift(savedShift);
      const states: Record<number, DayState> = {};
      preview.days.forEach((d: PreviewDay) => {
        const saved = draft.day_states[String(d.day)] as DayState | undefined;
        states[d.day] = saved ?? {
          shift: savedShift,
          ranges: [],
          extra_note: "",
        };
      });
      setDayStates(states);
      setDayUpdatedAt((draft.day_updated_at as Record<string, string>) ?? {});
    } else {
      const states: Record<number, DayState> = {};
      preview.days.forEach((d: PreviewDay) => {
        states[d.day] = {
          shift: defaultShift,
          ranges: [],
          extra_note: "",
        };
      });
      setDayStates(states);
      setDayUpdatedAt({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preview, draft, isDraftLoading]);

  const applyDefaultShift = (s: 7 | 8 | 9) => {
    setDefaultShift(s);
    setDayStates((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[Number(k)] = { ...next[Number(k)], shift: s };
      });
      return next;
    });
  };

  const applyUzPreset = (from: string, to: string) => {
    if (!preview?.days) return;
    setDayStates((prev) => {
      const next = { ...prev };
      (preview.days as PreviewDay[]).forEach((d) => {
        if (d.is_weekend || d.before_employment) {
          next[d.day] = { ...next[d.day], ranges: [] };
        } else if (
          !d.leave_label &&
          !d.sick_leave_label &&
          !d.other_leave_label
        ) {
          next[d.day] = { ...next[d.day], ranges: [{ from, to }] };
        }
      });
      return next;
    });
  };

  const setDayShift = (day: number, shift: 7 | 8 | 9) =>
    setDayStates((prev) => ({
      ...prev,
      [day]: { ...prev[day], shift: prev[day]?.shift === shift ? null : shift },
    }));

  const setDayNote = (day: number, note: string) =>
    setDayStates((prev) => ({
      ...prev,
      [day]: { ...prev[day], extra_note: note },
    }));

  const touchDay = (day: number) =>
    setDayUpdatedAt((prev) => ({
      ...prev,
      [String(day)]: new Date().toISOString(),
    }));

  const addRange = (day: number) => {
    touchDay(day);
    setDayStates((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        ranges: [...(prev[day]?.ranges ?? []), { from: "", to: "" }],
      },
    }));
  };

  const removeRange = (day: number, idx: number) => {
    touchDay(day);
    setDayStates((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        ranges: prev[day].ranges.filter((_, i) => i !== idx),
      },
    }));
  };

  const updateRange = (
    day: number,
    idx: number,
    field: "from" | "to",
    val: string,
  ) => {
    touchDay(day);
    setDayStates((prev) => {
      const ranges = [...(prev[day]?.ranges ?? [])];
      ranges[idx] = { ...ranges[idx], [field]: val };
      return { ...prev, [day]: { ...prev[day], ranges } };
    });
  };

  // For each UZ working day, compute which range indices overlap (if any)
  const overlapsByDay = useMemo<Record<number, Set<number>>>(() => {
    if (!usesRanges) return {};
    const result: Record<number, Set<number>> = {};
    Object.entries(dayStates).forEach(([dayStr, st]) => {
      const overlapping = getOverlappingIndices(st.ranges);
      if (overlapping.size > 0) result[Number(dayStr)] = overlapping;
    });
    return result;
  }, [usesRanges, dayStates]);

  const hasAnyOverlap = Object.keys(overlapsByDay).length > 0;

  // ── Walidacja kolizji UoP × UZ (between two contracts in the same month) ──
  // Pobieramy draft "drugiego" kontraktu (UoP jeśli edytujemy UZ, UZ jeśli UoP)
  // i wyliczamy nakładające się przedziały. Backend ma identyczną logikę w
  // `apps.timesheets.services.overlap`; tutaj duplikujemy dla szybkiego UX.
  const { data: otherDraft } = useQuery({
    queryKey: [
      "timesheet-pdf-other-draft",
      year,
      month,
      secondaryContractId,
      secondaryContracts.map((c) => c.id).join(","),
      employeeId,
    ],
    queryFn: async () => {
      // Edytujemy UoP → drugą stroną jest aktywna UZ dodatkowa (pierwsza z brzegu)
      if (!secondaryContractId) {
        const sc = secondaryContracts[0];
        if (!sc) return null;
        return timesheetsApi.getPdfDraft(year, month, sc.id, employeeId);
      }
      // Edytujemy UZ → drugą stroną jest UoP (primary)
      return timesheetsApi.getPdfDraft(year, month, null, employeeId);
    },
    enabled: open && secondaryContracts.length > 0,
    staleTime: 30_000,
  });

  const crossContractOverlaps = useMemo<
    { date: string; overlaps: { start: string; end: string }[] }[]
  >(() => {
    if (!preview?.days || !otherDraft?.day_states || isPartTimeUop) return [];
    const otherStates = otherDraft.day_states as Record<string, DayState>;
    const otherShiftDefault = otherDraft.default_shift ?? null;

    // Mapuje primary shift (7|8|9) na zakres [HH:MM, HH:MM).
    const shiftRange = (shift: number | null): [number, number] | null => {
      if (shift !== 7 && shift !== 8 && shift !== 9) return null;
      return [shift * 60, (shift + 8) * 60];
    };
    const toMins = (s: string) => {
      const [h, m] = s.split(":").map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    const fmt = (mins: number) =>
      `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

    const conflicts: {
      date: string;
      overlaps: { start: string; end: string }[];
    }[] = [];

    // currentEdited = którą stronę użytkownik aktualnie edytuje
    const editingUz = isUz;
    for (const d of preview.days as PreviewDay[]) {
      const dayKey = String(d.day);
      const isoDate = `${year}-${String(month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
      let uopShift: number | null;
      let uzRanges: { from: string; to: string }[];
      let uopIsWorking: boolean;

      if (editingUz) {
        // current = UZ, other = UoP
        const otherState = otherStates[dayKey];
        uopShift = (otherState?.shift as number | null) ?? otherShiftDefault;
        uopIsWorking = d.is_working;
        uzRanges = dayStates[d.day]?.ranges ?? [];
      } else {
        // current = UoP, other = UZ
        const currentShift = (dayStates[d.day]?.shift ?? defaultShift) as
          | 7
          | 8
          | 9
          | null;
        uopShift = d.is_working ? currentShift : null;
        uopIsWorking = d.is_working;
        const otherState = otherStates[dayKey];
        uzRanges = (otherState?.ranges as { from: string; to: string }[]) ?? [];
      }
      if (!uopIsWorking) continue;
      const range = shiftRange(uopShift);
      if (!range) continue;
      const overlaps: { start: string; end: string }[] = [];
      for (const r of uzRanges) {
        if (!r?.from || !r?.to) continue;
        const a = toMins(r.from);
        const b = toMins(r.to);
        if (b <= a) continue;
        const lo = Math.max(range[0], a);
        const hi = Math.min(range[1], b);
        if (lo < hi) overlaps.push({ start: fmt(lo), end: fmt(hi) });
      }
      if (overlaps.length > 0) {
        conflicts.push({ date: isoDate, overlaps });
      }
    }
    return conflicts;
  }, [
    preview,
    otherDraft,
    dayStates,
    defaultShift,
    isUz,
    isPartTimeUop,
    secondaryContractId,
    year,
    month,
  ]);

  const partTimeExpectedHours = useMemo<number | null>(() => {
    if (!isPartTimeUop || !preview?.days) return null;
    const workingDays = (preview.days as PreviewDay[]).filter((d) => d.is_working).length;
    return Math.round(workingDays * 8 * (fractionNum / fractionDen) * 10) / 10;
  }, [isPartTimeUop, preview, fractionNum, fractionDen]);

  const partTimeTotalHours = useMemo<number | null>(() => {
    if (!isPartTimeUop || !preview?.days) return null;
    let total = 0;
    for (const d of preview.days as PreviewDay[]) {
      if (d.leave_label || d.sick_leave_label || d.other_leave_label) continue;
      const st = dayStates[d.day];
      for (const r of st?.ranges ?? []) total += rangeHours(r.from, r.to);
    }
    return Math.round(total * 10) / 10;
  }, [isPartTimeUop, preview, dayStates]);

  const download = async () => {
    if (!preview?.days) return;
    setDownloading(true);
    setError("");
    try {
      const days = preview.days.map((d: PreviewDay) => {
        const st = dayStates[d.day];
        const noteParts = [d.auto_note, st?.extra_note].filter(Boolean);
        const note = noteParts.join(" — ");

        if (usesRanges) {
          // UZ or part-time UoP: editable on any day that is not an absence
          const uzEditable =
            !d.before_employment &&
            !d.leave_label &&
            !d.sick_leave_label &&
            !d.other_leave_label;
          const uzRanges = uzEditable ? (st?.ranges ?? []) : [];
          const hasFilledRanges = uzRanges.some((r) => r.from && r.to);
          return {
            day: d.day,
            date_str: d.date_str,
            weekday_short: d.weekday_short,
            is_working: hasFilledRanges,
            is_weekend: d.is_weekend,
            is_holiday: d.is_holiday,
            before_employment: d.before_employment ?? false,
            leave_label: d.leave_label,
            other_leave_label: d.other_leave_label,
            sick_leave_label: d.sick_leave_label,
            note,
            ranges: uzRanges,
            day_updated_at: dayUpdatedAt[String(d.day)] ?? null,
          };
        }

        return {
          day: d.day,
          date_str: d.date_str,
          weekday_short: d.weekday_short,
          is_working: d.is_working,
          is_weekend: d.is_weekend,
          is_holiday: d.is_holiday,
          before_employment: d.before_employment ?? false,
          leave_label: d.leave_label,
          other_leave_label: d.other_leave_label,
          sick_leave_label: d.sick_leave_label,
          shift: d.is_working
            ? st !== undefined
              ? st.shift
              : defaultShift
            : null,
          note,
        };
      });
      const blob = await timesheetsApi.exportPdf({
        year,
        month,
        days: isSecondaryUz ? [] : days,
        secondary_contract: secondaryContractId,
        declared_hours: isSecondaryUz ? declaredHours || null : null,
        employee: employeeId,
      });
      const suffix = secondaryContractId
        ? "_UZ_dodatkowa"
        : isUz
          ? "_UZ"
          : "_UoP";
      downloadBlob(blob, `karta_godzin${suffix}_${year}_${String(month).padStart(2, "0")}.pdf`);
    } catch {
      setError("Błąd podczas generowania PDF. Spróbuj ponownie.");
    } finally {
      setDownloading(false);
    }
  };

  // Build a day payload from preview + current dayStates honoring UoP/UZ mode.
  const buildDaysPayload = (mode: "uop" | "uz") => {
    if (!preview?.days) return [] as unknown[];
    return preview.days.map((d: PreviewDay) => {
      const st = dayStates[d.day];
      const noteParts = [d.auto_note, st?.extra_note].filter(Boolean);
      const note = noteParts.join(" — ");
      if (mode === "uz") {
        const uzEditable =
          !d.before_employment &&
          !d.leave_label &&
          !d.sick_leave_label &&
          !d.other_leave_label;
        const uzRanges = uzEditable ? (st?.ranges ?? []) : [];
        const hasFilledRanges = uzRanges.some((r) => r.from && r.to);
        return {
          day: d.day,
          date_str: d.date_str,
          weekday_short: d.weekday_short,
          is_working: hasFilledRanges,
          is_weekend: d.is_weekend,
          is_holiday: d.is_holiday,
          before_employment: d.before_employment ?? false,
          leave_label: d.leave_label,
          other_leave_label: d.other_leave_label,
          sick_leave_label: d.sick_leave_label,
          note,
          ranges: uzRanges,
        };
      }
      return {
        day: d.day,
        date_str: d.date_str,
        weekday_short: d.weekday_short,
        is_working: d.is_working,
        is_weekend: d.is_weekend,
        is_holiday: d.is_holiday,
        before_employment: d.before_employment ?? false,
        leave_label: d.leave_label,
        other_leave_label: d.other_leave_label,
        sick_leave_label: d.sick_leave_label,
        shift: d.is_working
          ? st !== undefined
            ? st.shift
            : defaultShift
          : null,
        note,
      };
    });
  };

  const bulkDownload = async () => {
    if (!preview?.days || secondaryContracts.length === 0) return;
    setBulkDownloading(true);
    setError("");
    try {
      const primaryDays = buildDaysPayload(
        usesRanges && !secondaryContractId ? "uz" : "uop",
      );
      // Use the currently-selected secondary if any; otherwise pick first active.
      const sc = secondaryContractId
        ? secondaryContracts.find((c) => c.id === secondaryContractId)
        : secondaryContracts[0];
      if (!sc) return;
      // Fetch the OTHER side's day_states from its saved draft so we don't
      // overwrite anything that wasn't open in the modal.
      const secondaryDraft = await timesheetsApi.getPdfDraft(
        year,
        month,
        sc.id,
        employeeId,
      );
      const primaryDraft = await timesheetsApi.getPdfDraft(year, month, null, employeeId);
      // For days that aren't in our active state, fall back to the OTHER draft.
      const secondaryDayStates = (secondaryDraft?.day_states ?? {}) as Record<
        string,
        DayState
      >;
      const primaryDayStates = (primaryDraft?.day_states ?? {}) as Record<
        string,
        DayState
      >;
      // Build secondary days payload from the secondary draft (UZ shape).
      const secondaryDays = preview.days.map((d: PreviewDay) => {
        const st = secondaryDayStates[String(d.day)] as DayState | undefined;
        const ranges = d.before_employment ? [] : (st?.ranges ?? []);
        const hasFilled = ranges.some((r) => r.from && r.to);
        const note = [d.auto_note, st?.extra_note].filter(Boolean).join(" — ");
        return {
          day: d.day,
          date_str: d.date_str,
          weekday_short: d.weekday_short,
          is_working: hasFilled,
          is_weekend: d.is_weekend,
          is_holiday: d.is_holiday,
          before_employment: d.before_employment ?? false,
          leave_label: d.leave_label,
          other_leave_label: d.other_leave_label,
          sick_leave_label: d.sick_leave_label,
          note,
          ranges,
        };
      });
      // Primary days from primary draft (UoP shape), unless current mode is UoP
      // in which case use buildDaysPayload (it has the live state).
      const primaryFromDraft = preview.days.map((d: PreviewDay) => {
        const st = primaryDayStates[String(d.day)] as DayState | undefined;
        const shift = (st?.shift ?? defaultShift) as 7 | 8 | 9 | null;
        const note = [d.auto_note, st?.extra_note].filter(Boolean).join(" — ");
        return {
          day: d.day,
          date_str: d.date_str,
          weekday_short: d.weekday_short,
          is_working: d.is_working,
          is_weekend: d.is_weekend,
          is_holiday: d.is_holiday,
          before_employment: d.before_employment ?? false,
          leave_label: d.leave_label,
          other_leave_label: d.other_leave_label,
          sick_leave_label: d.sick_leave_label,
          shift: d.is_working ? shift : null,
          note,
        };
      });
      // Additional UZ is rendered in flat ryczałt mode when declared_hours is
      // set — without it the backend falls back to per-day rendering, producing
      // a PDF that differs from the single-file download.
      const secondaryDeclaredHours =
        secondaryContractId === sc.id
          ? declaredHours || null
          : ((secondaryDraft as { declared_hours?: string | null } | null)
              ?.declared_hours ?? null);
      const blob = await timesheetsApi.bulkExportPdf({
        year,
        month,
        primary: { days: secondaryContractId ? primaryFromDraft : primaryDays },
        secondary: {
          contract_id: sc.id,
          days: secondaryDays,
          declared_hours: secondaryDeclaredHours,
        },
        employee: employeeId,
      });
      downloadBlob(blob, `karty_godzin_${year}_${String(month).padStart(2, "0")}.zip`);
    } catch {
      setError(
        "Błąd podczas generowania PDF (dwa kontrakty). Spróbuj ponownie.",
      );
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleClose = () => {
    initializedPeriodRef.current = "";
    setDayStates({});
    setDayUpdatedAt({});
    onClose();
  };


  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={forEmployee ? `Generuj kartę godzin — ${forEmployee.name}` : "Generuj kartę godzin (PDF)"}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        {/* Period + default shift (UoP only) */}
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-44">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Okres
            </label>
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={`${year}-${month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                initializedPeriodRef.current = "";
                setSaveSuccess(false);
                setYear(y);
                setMonth(m);
                setDayStates({});
              }}
            >
              {PERIODS.map((p) => (
                <option
                  key={`${p.year}-${p.month}`}
                  value={`${p.year}-${p.month}`}
                >
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {secondaryContracts.length > 0 && (
            <div className="flex-1 min-w-56">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Karta godzin dla
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                value={secondaryContractId ?? ""}
                onChange={(e) => {
                  initializedPeriodRef.current = "";
                  setSaveSuccess(false);
                  setSecondaryContractId(e.target.value || null);
                  setDayStates({});
                }}
              >
                <option value="">{primaryLabel}</option>
                {secondaryContracts.map((c) => {
                  const company = c.company_short_code ?? c.company_name;
                  return (
                    <option key={c.id} value={c.id}>
                      Umowa zlecenie{company ? ` — ${company}` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {!usesRanges && !isSecondaryUz && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Domyślna zmiana{" "}
                <span className="text-xs text-gray-400 font-normal">
                  (stosowana do wszystkich dni)
                </span>
              </label>
              <div className="flex gap-1">
                {([7, 8, 9] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => applyDefaultShift(s)}
                    className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      defaultShift === s
                        ? "border-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/30 text-[var(--color-primary)] dark:text-blue-300"
                        : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    {s}:00–{s + 8}:00
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Presety pełnoetatowe — tylko dla pracownika z UZ jako podstawowy
              typ zatrudnienia. Dodatkowa UZ jest z definicji dorywcza, więc
              nie ma sensu wypełniać domyślnie wszystkich dni roboczych. */}
          {isUz && !secondaryContractId && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Wypełnij dni robocze{" "}
                <span className="text-xs text-gray-400 font-normal">
                  (stosowane do wszystkich dostępnych dni)
                </span>
              </label>
              <div className="flex flex-wrap gap-1">
                {UZ_PRESETS.map((p) => (
                  <button
                    key={p.from}
                    onClick={() => applyUzPreset(p.from, p.to)}
                    className="px-3 py-2 rounded-md text-sm font-medium border transition-colors border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Legend — only for per-day mode (primary contract). */}
        {!isSecondaryUz && (
          <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-700 inline-block" />{" "}
              Weekend
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-700 inline-block" />{" "}
              Święto
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-700 inline-block" />{" "}
              Nieobecność
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-700 inline-block" />{" "}
              Urlop inny
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-700 inline-block" />{" "}
              L4
            </span>
          </div>
        )}

        {/* Warning: kolizja godzin UoP × UZ (dwa kontrakty tego samego pracownika
            mają nakładające się godziny w tym samym dniu). Skipped in
            declared-hours mode (additional UZ has no per-day schedule). */}
        {!isSecondaryUz && crossContractOverlaps.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2.5 flex gap-2 items-start">
            <AlertTriangle
              size={16}
              className="flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400"
            />
            <div className="text-xs text-amber-800 dark:text-amber-300 space-y-1">
              <p className="font-semibold">
                Godziny umów się nakładają — popraw przed pobraniem PDF.
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                {crossContractOverlaps.slice(0, 5).map((c) => (
                  <li key={c.date}>
                    <span className="font-mono">{formatDate(c.date)}</span>:{" "}
                    {c.overlaps.map((o, i) => (
                      <span key={i}>
                        {i > 0 && ", "}
                        <span className="font-mono">
                          {o.start}–{o.end}
                        </span>
                      </span>
                    ))}{" "}
                    — pokrywa się z{" "}
                    {isUz ? "umową podstawową (UoP)" : "umową dodatkową (UZ)"}.
                  </li>
                ))}
                {crossContractOverlaps.length > 5 && (
                  <li className="italic">
                    …i jeszcze {crossContractOverlaps.length - 5}{" "}
                    {crossContractOverlaps.length - 5 === 1 ? "dzień" : "dni"} z
                    kolizjami.
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Flat declared-hours form — only for additional UZ */}
        {isSecondaryUz && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Liczba przepracowanych godzin
                <span className="ml-1 text-xs text-gray-400 font-normal">
                  (deklarowana suma za cały miesiąc)
                </span>
              </label>
              <input
                type="number"
                inputMode="decimal"
                step="0.25"
                min="0"
                max="744"
                className="block w-40 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-mono text-right"
                value={declaredHours}
                onChange={(e) => setDeclaredHours(e.target.value)}
                placeholder="np. 40"
              />
            </div>

            {(() => {
              const sc = secondaryContracts.find(
                (c) => c.id === secondaryContractId,
              );
              const isMonthly = sc?.rate_type === "monthly";
              const hours = Number(declaredHours);
              const rate = sc ? Number(sc.hourly_gross_rate) : 0;
              const gross = isMonthly
                ? (rate > 0 ? rate.toFixed(2) : "—")
                : (hours > 0 && rate > 0 ? (hours * rate).toFixed(2) : "—");
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm rounded-md bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">
                      Godziny
                    </div>
                    <div className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                      {hours > 0 ? hours : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">
                      Stawka
                    </div>
                    <div className="font-mono text-gray-700 dark:text-gray-300">
                      {sc
                        ? `${formatAmount(sc.hourly_gross_rate)} ${isMonthly ? "zł/mies." : "zł/h"}`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">
                      Brutto
                    </div>
                    <div className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                      {gross !== "—" ? `${gross} zł` : "—"}
                    </div>
                  </div>
                </div>
              );
            })()}

            <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">
              UZ dodatkowa rozliczana ryczałtem — bez harmonogramu per dzień.
              Wypełniasz tylko sumę godzin przepracowanych w miesiącu.
            </p>
          </div>
        )}

        {/* Days table — primary contract only */}
        {!isSecondaryUz && isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : !isSecondaryUz && preview?.days ? (
          <div className="max-h-[380px] overflow-y-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-2 py-2 text-left w-16 font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Data
                  </th>
                  <th className="px-2 py-2 text-left w-10 font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Dz.
                  </th>
                  <th className="px-2 py-2 text-center font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    {usesRanges ? "Zakresy godzin" : "Zmiana"}
                  </th>
                  <th className="px-2 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Nieobecność / Uwagi
                  </th>
                  {usesRanges && (
                    <th className="px-2 py-2 text-left w-24 font-mono text-xs uppercase tracking-widest text-gray-400 dark:text-gray-500">
                      Ost. zmiana
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(preview.days as PreviewDay[]).map((d) => {
                  const st = dayStates[d.day] ?? {
                    shift: defaultShift,
                    ranges: [],
                    extra_note: "",
                  };
                  const uzEditable =
                    usesRanges &&
                    !d.before_employment &&
                    !d.leave_label &&
                    !d.sick_leave_label &&
                    !d.other_leave_label &&
                    !(isPartTimeUop && (d.is_weekend || d.is_holiday));
                  const rowBg = d.is_weekend
                    ? "bg-gray-50 dark:bg-gray-800/40"
                    : d.is_holiday
                      ? "bg-yellow-50 dark:bg-yellow-900/10"
                      : d.sick_leave_label
                        ? "bg-purple-50 dark:bg-purple-900/10"
                        : d.leave_label
                          ? "bg-blue-50 dark:bg-blue-900/10"
                          : "bg-white dark:bg-gray-800";

                  return (
                    <tr
                      key={d.day}
                      className={`border-t border-gray-100 dark:border-gray-700/50 ${
                        d.is_weekend ||
                        d.before_employment ||
                        d.is_holiday ||
                        d.sick_leave_label ||
                        d.leave_label ||
                        d.other_leave_label
                          ? "bg-gray-50 dark:bg-gray-800/40"
                          : "bg-white dark:bg-gray-800"
                      }`}
                    >
                      {/* Date */}
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span
                          className={`font-semibold ${d.is_weekend || d.before_employment ? "text-gray-400" : "text-gray-800 dark:text-gray-200"}`}
                        >
                          {d.date_str}
                        </span>
                      </td>

                      {/* Day name */}
                      <td
                        className={`px-2 py-1.5 ${d.is_weekend || d.before_employment ? "text-gray-400" : "text-gray-500 dark:text-gray-400"}`}
                      >
                        {d.weekday_short}
                      </td>

                      {/* Shift (UoP) or time ranges (UZ) */}
                      <td className="px-2 py-1">
                        {usesRanges ? (
                          uzEditable ? (
                            <div className="flex flex-col gap-1">
                              {st.ranges.map((r, i) => {
                                const h =
                                  r.from && r.to ? rangeHours(r.from, r.to) : 0;
                                const conflict =
                                  overlapsByDay[d.day]?.has(i) ?? false;
                                return (
                                  <div
                                    key={i}
                                    className="flex items-center gap-1"
                                  >
                                    <input
                                      type="time"
                                      value={r.from}
                                      onChange={(e) =>
                                        updateRange(
                                          d.day,
                                          i,
                                          "from",
                                          e.target.value,
                                        )
                                      }
                                      className={`w-[5.5rem] rounded border px-1 py-0.5 text-xs text-center bg-white dark:bg-gray-700 ${
                                        conflict
                                          ? "border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20"
                                          : "border-gray-200 dark:border-gray-600"
                                      }`}
                                    />
                                    <span className="text-gray-400 select-none">
                                      –
                                    </span>
                                    <input
                                      type="time"
                                      value={r.to}
                                      onChange={(e) =>
                                        updateRange(
                                          d.day,
                                          i,
                                          "to",
                                          e.target.value,
                                        )
                                      }
                                      className={`w-[5.5rem] rounded border px-1 py-0.5 text-xs text-center bg-white dark:bg-gray-700 ${
                                        conflict
                                          ? "border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20"
                                          : "border-gray-200 dark:border-gray-600"
                                      }`}
                                    />
                                    {h > 0 && !conflict && (
                                      <span className="text-gray-400 w-7 text-right tabular-nums">
                                        {formatHours(h)}
                                      </span>
                                    )}
                                    {conflict && (
                                      <span
                                        title="Zakresy się pokrywają"
                                        className="text-red-500 w-7 flex justify-center"
                                      >
                                        <AlertTriangle size={11} />
                                      </span>
                                    )}
                                    <button
                                      onClick={() => removeRange(d.day, i)}
                                      className="text-gray-300 hover:text-red-400 transition-colors ml-0.5"
                                      title="Usuń zakres"
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                );
                              })}
                              <button
                                onClick={() => addRange(d.day)}
                                className="flex items-center gap-0.5 text-[10px] text-[var(--color-primary)] hover:underline mt-0.5 w-fit"
                              >
                                <Plus size={9} /> dodaj zakres
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600">
                              —
                            </span>
                          )
                        ) : d.is_working ? (
                          <div className="flex gap-0.5 justify-center">
                            {([7, 8, 9] as const).map((s) => (
                              <button
                                key={s}
                                onClick={() => setDayShift(d.day, s)}
                                className={`w-8 h-6 rounded text-xs font-medium transition-colors ${
                                  st.shift === s
                                    ? "bg-[var(--color-primary)] text-white shadow-sm"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }`}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">
                            —
                          </span>
                        )}
                      </td>

                      {/* Note */}
                      <td className="px-2 py-1">
                        <div className="flex items-center gap-1.5">
                          {d.is_holiday && (
                            <Sun
                              size={11}
                              className="text-gray-400 flex-shrink-0"
                            />
                          )}
                          {d.sick_leave_label && !d.is_holiday && (
                            <Stethoscope
                              size={11}
                              className="text-gray-400 flex-shrink-0"
                            />
                          )}
                          {d.leave_label &&
                            !d.sick_leave_label &&
                            !d.is_holiday && (
                              <Umbrella
                                size={11}
                                className="text-gray-400 flex-shrink-0"
                              />
                            )}
                          {d.other_leave_label &&
                            !d.sick_leave_label &&
                            !d.leave_label &&
                            !d.is_holiday && (
                              <BookOpen
                                size={11}
                                className="text-gray-400 flex-shrink-0"
                              />
                            )}
                          {d.auto_note && (
                            <span className="text-gray-500 dark:text-gray-400 font-medium">
                              {d.auto_note}
                            </span>
                          )}
                          {(usesRanges ? uzEditable : d.is_working) && (
                            <input
                              type="text"
                              value={st.extra_note}
                              onChange={(e) =>
                                setDayNote(d.day, e.target.value)
                              }
                              placeholder="dodatkowa uwaga..."
                              className="flex-1 min-w-0 bg-transparent border-none outline-none text-xs text-gray-500 dark:text-gray-400 placeholder-gray-300 dark:placeholder-gray-600"
                            />
                          )}
                          {d.is_weekend && !d.leave_label && !usesRanges && (
                            <span className="text-gray-300 dark:text-gray-600 flex items-center gap-0.5">
                              <Coffee size={10} /> wolne
                            </span>
                          )}
                        </div>
                      </td>
                      {usesRanges && (
                        <td className="px-2 py-1 text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                          {formatDateTime(dayUpdatedAt[String(d.day)], "")}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {!isSecondaryUz && hasAnyOverlap && (
          <div className="flex items-center gap-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-400">
            <AlertTriangle size={13} className="flex-shrink-0" />
            Niektóre zakresy godzin w tym samym dniu pokrywają się. Popraw je
            przed pobraniem PDF.
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {saveSuccess && (
          <p className="text-sm text-green-600 dark:text-green-400">
            Ustawienia zostały zapisane.
          </p>
        )}

        {isPartTimeUop && partTimeExpectedHours !== null && partTimeTotalHours !== null && (
          partTimeTotalHours < partTimeExpectedHours ? (
            <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2.5 flex gap-2 items-start">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
              <p className="text-xs text-red-800 dark:text-red-300">
                <span className="font-semibold">Niewystarczająca liczba godzin.</span>{" "}
                Wpisano {formatHours(partTimeTotalHours)} z wymaganych{" "}
                {formatHours(partTimeExpectedHours)}{" "}
                ({fractionNum}/{fractionDen} etatu ×{" "}
                {(preview?.days as PreviewDay[] | undefined)?.filter((d) => d.is_working).length ?? 0}{" "}
                dni rob. × 8 h). PDF niedostępny.
              </p>
            </div>
          ) : partTimeTotalHours > partTimeExpectedHours ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2.5 flex gap-2 items-start">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                <span className="font-semibold">Przekroczona liczba godzin.</span>{" "}
                Wpisano {formatHours(partTimeTotalHours)}, oczekiwano{" "}
                {formatHours(partTimeExpectedHours)} ({fractionNum}/{fractionDen} etatu).
                Sprawdź wpisy — możesz jednak pobrać PDF.
              </p>
            </div>
          ) : null
        )}
        <DraftHistoryTimeline
          year={year}
          month={month}
          secondaryContractId={secondaryContractId}
          employeeId={employeeId}
          title={forEmployee ? `Historia zmian — ${forEmployee.name}` : "Twoja historia zmian"}
        />

        <div className="flex flex-wrap gap-3">
          <Button
            loading={downloading}
            onClick={download}
            disabled={
              !preview ||
              isLoading ||
              (!isSecondaryUz &&
                (hasAnyOverlap || crossContractOverlaps.length > 0)) ||
              (isSecondaryUz && (!declaredHours || Number(declaredHours) <= 0)) ||
              (isPartTimeUop && partTimeTotalHours !== null && partTimeExpectedHours !== null && partTimeTotalHours < partTimeExpectedHours)
            }
          >
            <Download size={14} className="mr-1.5" /> Pobierz PDF
          </Button>
          {secondaryContracts.length > 0 && (
            <Button
              loading={bulkDownloading}
              onClick={bulkDownload}
              disabled={
                !preview ||
                isLoading ||
                (!isSecondaryUz &&
                  (hasAnyOverlap || crossContractOverlaps.length > 0)) ||
                (isSecondaryUz &&
                  (!declaredHours || Number(declaredHours) <= 0)) ||
                (isPartTimeUop && partTimeTotalHours !== null && partTimeExpectedHours !== null && partTimeTotalHours < partTimeExpectedHours)
              }
              variant="secondary"
              title="Generuje dwa PDFy (UoP + UZ-dodatkowa) w jednym ZIP-ie. Każdy z osobnym podpisem przełożonego."
            >
              <Download size={14} className="mr-1.5" /> Pobierz oba (UoP + UZ) —
              ZIP
            </Button>
          )}
          {!forEmployee && (
            <Button
              variant="secondary"
              loading={saveMutation.isPending}
              onClick={() => {
                setError("");
                saveMutation.mutate();
              }}
              disabled={!preview || isLoading}
            >
              <Save size={14} className="mr-1.5" /> Zapisz
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>
            Anuluj
          </Button>
        </div>
      </div>
    </Modal>
  );
}
